import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FAMILY_RELATION, type FamilyRelation } from '@his/shared';
import { get, patch } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker, type PatientSummary } from './PatientPicker';

export type FamilyCardRelation = FamilyRelation | 'relative';

export interface FamilyMember {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  dob: string | null;
  ageLabel: string;
  phone: string | null;
  relation: FamilyCardRelation;
}

export interface FamilyCard {
  groupId: string | null;
  members: FamilyMember[];
}

const RELATION_LABEL: Record<FamilyCardRelation, () => string> = {
  mother: () => tr('opd', 'relation_mother'),
  father: () => tr('opd', 'relation_father'),
  parent: () => tr('opd', 'relation_parent'),
  child: () => tr('opd', 'relation_child'),
  spouse: () => tr('opd', 'relation_spouse'),
  sibling: () => tr('opd', 'relation_sibling'),
  other: () => tr('opd', 'relation_other'),
  relative: () => tr('opd', 'relation_relative'),
};

export function familyRelationLabel(relation: string): string {
  const label = RELATION_LABEL[relation as FamilyCardRelation];
  return label ? label() : relation;
}

export function PatientFamilyList({ patientId, patientName, canEdit }: { patientId: string; patientName: string; canEdit: boolean }) {
  const [linking, setLinking] = useState(false);
  const family = useQuery({ queryKey: ['patient-family', patientId], queryFn: () => get<FamilyCard>(`/patients/${patientId}/family`) });

  return (
    <div className="space-y-1.5">
      {family.isLoading && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Spinner /> {tr('opd', 'loading_the_family')}
        </p>
      )}
      {family.data && family.data.members.length === 0 && <p className="text-muted-foreground">{tr('opd', 'no_family_links_yet')}</p>}
      {family.data?.members.map((member) => (
        <div key={member.id} className="flex items-start justify-between gap-2 rounded-md border px-2 py-1.5">
          <span className="min-w-0">
            <Link to={`/patients/${member.id}`} className="font-medium underline-offset-4 hover:underline">
              {member.fullName}
            </Link>
            <span className="block text-xs text-muted-foreground">
              {member.mrn} · {member.ageLabel}
              {member.phone ? ` · ${member.phone}` : ''}
            </span>
          </span>
          <Badge variant="secondary">{familyRelationLabel(member.relation)}</Badge>
        </div>
      ))}
      {canEdit && (
        <Button variant="outline" size="sm" className="w-full" onClick={() => setLinking(true)}>
          {tr('opd', 'link_a_family_member')}
        </Button>
      )}
      {linking && <LinkFamilyDialog patientId={patientId} patientName={patientName} onClose={() => setLinking(false)} />}
    </div>
  );
}

function LinkFamilyDialog({ patientId, patientName, onClose }: { patientId: string; patientName: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [relative, setRelative] = useState<PatientSummary | null>(null);
  const [relation, setRelation] = useState<FamilyRelation>('mother');
  const [error, setError] = useState<string | null>(null);

  const link = useMutation({
    mutationFn: () => patch<FamilyCard>(`/patients/${patientId}/family`, { parentPatientId: relative?.id, relation }),
    onSuccess: () => {
      toast.success(tr('opd', 'family_member_linked'));
      void queryClient.invalidateQueries({ queryKey: ['patient-family', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'link_x_to_a_family_member', { name: patientName })}
      description={tr('opd', 'the_record_joins_the_other_patients_family')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              link.mutate();
            }}
            loading={link.isPending}
            disabled={!relative || relative.id === patientId}
          >
            {tr('opd', 'link')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Field label={tr('opd', 'linked_to')} required>
          <PatientPicker value={relative?.id ?? ''} onChange={(id, patient) => setRelative(id && patient ? patient : null)} excludeId={patientId} autoFocus />
        </Field>
        <Field label={tr('opd', 'the_linked_patient_is_this_patients')} required>
          <Select value={relation} onChange={(e) => setRelation(e.target.value as FamilyRelation)}>
            {FAMILY_RELATION.map((value) => (
              <option key={value} value={value}>
                {familyRelationLabel(value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
